'use client';

import React, { useMemo, useState } from 'react';
import { Grid2x2, LayoutGrid, Monitor, Search } from 'lucide-react';
import { cn } from '@/utils';
import useVesselsStore from '@/store/vessels';
import VesselMapCard from './VesselMapCard';

export default function GridMaps() {
  const vessels = useVesselsStore((state) => state.vessels);
  const [query, setQuery] = useState('');
  const [cols, setCols] = useState<2 | 4 | 'auto'>('auto');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return vessels;
    return vessels.filter(
      (v) =>
        v.vessel_name?.toLowerCase().includes(q) ||
        v.id?.toLowerCase().includes(q) ||
        v.ship_type?.toLowerCase().includes(q) ||
        v.imo?.toLowerCase().includes(q) ||
        v.mmsi?.toLowerCase().includes(q) ||
        v.device_type?.toLowerCase().includes(q),
    );
  }, [vessels, query]);

  const colsClass =
    cols === 2
      ? 'grid-cols-1 md:grid-cols-2'
      : cols === 4
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(320px,1fr))]';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Grid Maps</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Live position of {vessels.length} vessels, one mini-map per asset.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search vessel, IMO, type…"
              className="h-10 pl-10 pr-3 w-72 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div className="flex p-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {[
              { n: 2 as const, Icon: LayoutGrid, label: '2 Columns' },
              { n: 4 as const, Icon: Grid2x2, label: '4 Columns' },
              { n: 'auto' as const, Icon: Monitor, label: 'Responsive Auto' },
            ].map(({ n, Icon, label }) => (
              <button
                key={n}
                onClick={() => setCols(n)}
                aria-label={label}
                title={label}
                className={cn(
                  'h-8 w-8 rounded-md flex items-center justify-center text-xs font-medium transition-colors',
                  cols === n
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
                )}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
      </header>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900">
          No vessels match “{query}”.
        </div>
      ) : (
        <div className={cn('grid gap-4', colsClass)}>
          {filtered.map((v) => (
            <VesselMapCard key={v.id} vessel={v} />
          ))}
        </div>
      )}
    </div>
  );
}
